import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Unlink } from 'lucide-react';
import { useSettings, formatNumber } from '../context/SettingsContext';
import { formatDate, formatDatetime } from '../utils/dates';
import type { ImportedBooking, BookingBalance, PaymentAccount, BookingExtra } from '../api';
import {
  updateImportedBooking, getBookingBalance, getBookingById,
  getPaymentAccounts, createManualPosting, deleteManualPosting, setPostingMatch,
  getBookingExtras, saveBookingExtras,
} from '../api';

interface Props {
  invoice: ImportedBooking;
  onClose: () => void;
  onSave: (updated: ImportedBooking) => void;
}

function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-gray-100 last:border-0">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-800 font-medium text-right">{value}</dd>
    </div>
  );
}

function Section({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">{title}</h3>
      <dl>{children}</dl>
    </div>
  );
}

// Input-felt brugt i edit-mode. Skal defineres uden for parent-komponenten,
// ellers re-mounter React den ved hver render og inputtet mister fokus efter hvert tegn.
function FormField({ label, type = 'number', step = '0.01', value, onChange }: {
  label: string;
  type?: string;
  step?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-0.5">{label}</label>
      <input
        type={type}
        step={type === 'number' ? step : undefined}
        value={value}
        onChange={onChange}
        className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--color-border-focus)]"
        placeholder="—"
      />
    </div>
  );
}

type FormData = {
  invoice_number: string; invoice_date: string;
  rental_start: string; rental_end: string;
  rental_amount: string; dynamic_pricing: string;
  km_extra_amount: string; km_overage_amount: string;
  excess_reduction: string; excess_risk_amount: string;
  fuel_adjustment: string; late_return_fee: string;
  extra_driver_fee: string; foreign_driving_fee: string;
  handling_fees: string; discount: string; payout: string;
  km_start: string; km_end: string; km_included: string; km_extra: string;
  fuel_start_pct: string; fuel_end_pct: string;
};

// Lokal type til extras i edit-mode (id er null før første gem)
type ExtraRow = { id: number | null; description: string; amount: string; vat_applicable: boolean };

// Konvertér MySQL datetime "2026-04-30 11:15:00" til <input type=datetime-local> "2026-04-30T11:15"
const dtToInput = (s: string | null | undefined): string => {
  if (!s) return '';
  return String(s).replace(' ', 'T').slice(0, 16);
};
// Tilbage til "2026-04-30 11:15:00" til DB
const inputToDt = (s: string): string | null => {
  if (!s) return null;
  return s.replace('T', ' ') + ':00';
};

const toStr = (n: number | null | undefined) => n != null ? String(n) : '';

function initForm(inv: ImportedBooking): FormData {
  return {
    invoice_number:     inv.invoice_number    ?? '',
    invoice_date:       inv.invoice_date      ?? '',
    rental_start:       dtToInput(inv.rental_start),
    rental_end:         dtToInput(inv.rental_end),
    rental_amount:      toStr(inv.rental_amount),
    dynamic_pricing:    toStr(inv.dynamic_pricing),
    km_extra_amount:    toStr(inv.km_extra_amount),
    km_overage_amount:  toStr(inv.km_overage_amount),
    excess_reduction:   toStr(inv.excess_reduction),
    excess_risk_amount: toStr(inv.excess_risk_amount),
    fuel_adjustment:    toStr(inv.fuel_adjustment),
    late_return_fee:    toStr(inv.late_return_fee),
    extra_driver_fee:   toStr(inv.extra_driver_fee),
    foreign_driving_fee: toStr(inv.foreign_driving_fee),
    handling_fees:      toStr(inv.handling_fees),
    discount:           toStr(inv.discount),
    payout:             toStr(inv.payout),
    km_start:           toStr(inv.km_start),
    km_end:             toStr(inv.km_end),
    km_included:        toStr(inv.km_included),
    km_extra:           toStr(inv.km_extra),
    fuel_start_pct:     toStr(inv.fuel_start_pct),
    fuel_end_pct:       toStr(inv.fuel_end_pct),
  };
}

export default function BookingDetailModal({ invoice: invProp, onClose, onSave }: Props) {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const fmt = (n: number | null | undefined) => n != null ? formatNumber(n, settings.numberFormat) + ' DKK' : '—';
  const tz = settings.timezone || 'Europe/Copenhagen';

  // Lokal kopi af booking-data så ændringer fra betalings-flowet (fee opdaterer booking.handling_fees + payout)
  // reflekteres straks i UI'et uden at vente på at parent re-fetcher.
  const [inv, setInv] = useState<ImportedBooking>(invProp);
  useEffect(() => { setInv(invProp); }, [invProp]);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormData>(() => initForm(invProp));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [balance, setBalance] = useState<BookingBalance | null>(null);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [confirmPosting, setConfirmPosting] = useState<number | null>(null);

  // Ekstra fakturalinjer (fx "Afhentning i Randers", "Mistet lejeindtægt") som ikke passer ind i
  // GoMore's faste komponenter. Indlæses sammen med bookingen og gemmes ved Gem i edit-mode.
  const [extras, setExtras] = useState<BookingExtra[]>([]);
  const [editExtras, setEditExtras] = useState<ExtraRow[]>([]);

  const reloadExtras = useCallback(async () => {
    try { setExtras(await getBookingExtras(invProp.id)); } catch { /* ignorer */ }
  }, [invProp.id]);

  useEffect(() => { reloadExtras(); }, [reloadExtras]);

  // Genindlæs balance + booking-data. Bruges efter manuel betaling med gebyr opdaterer booking.handling_fees.
  const refreshAll = useCallback(async () => {
    try {
      const [bal, fresh] = await Promise.all([getBookingBalance(invProp.id), getBookingById(invProp.id)]);
      setBalance(bal);
      setInv(fresh);
      onSave(fresh);  // propager opdateringen til parent så listen også opdaterer
    } catch { /* ignorér */ }
  }, [invProp.id, onSave]);

  useEffect(() => {
    let cancelled = false;
    getBookingBalance(invProp.id)
      .then(b => { if (!cancelled) setBalance(b); })
      .catch(() => { /* postering kan blot ikke findes — ignorér */ });
    return () => { cancelled = true; };
  }, [invProp.id]);

  const handleDeletePosting = async (id: number) => {
    try { await deleteManualPosting(id); await refreshAll(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Kunne ikke slette betaling'); }
  };

  // Kobl en GoMore-postering fra bookingen. Posteringen bliver på kontoen og står som ikke matchet.
  const handleUnmatchPosting = async (id: number) => {
    try { await setPostingMatch(id, null); await refreshAll(); }
    catch (e) { setError(e instanceof Error ? e.message : t('rental.posting_unmatch_error')); }
  };

  const set = (key: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleEdit = () => {
    setForm(initForm(inv));
    setEditExtras(extras.map(e => ({
      id: e.id,
      description: e.description,
      amount: String(e.amount),
      vat_applicable: !!e.vat_applicable,
    })));
    setEditing(true);
    setError(null);
  };
  const handleCancel = () => { setEditing(false); setError(null); };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const toNum = (s: string) => s === '' ? null : parseFloat(s);
    const toInt = (s: string) => s === '' ? null : parseInt(s, 10);
    try {
      const payload: Record<string, unknown> = {
        invoice_number:     form.invoice_number || null,
        invoice_date:       form.invoice_date || null,
        rental_amount:      toNum(form.rental_amount),
        dynamic_pricing:    toNum(form.dynamic_pricing),
        km_extra_amount:    toNum(form.km_extra_amount),
        km_overage_amount:  toNum(form.km_overage_amount),
        excess_reduction:   toNum(form.excess_reduction),
        excess_risk_amount: toNum(form.excess_risk_amount),
        fuel_adjustment:    toNum(form.fuel_adjustment),
        late_return_fee:    toNum(form.late_return_fee),
        extra_driver_fee:   toNum(form.extra_driver_fee),
        foreign_driving_fee: toNum(form.foreign_driving_fee),
        handling_fees:      toNum(form.handling_fees),
        discount:           toNum(form.discount),
        payout:             toNum(form.payout),
        km_start:           toInt(form.km_start),
        km_end:             toInt(form.km_end),
        km_included:        toInt(form.km_included),
        km_extra:           toInt(form.km_extra),
        fuel_start_pct:     toInt(form.fuel_start_pct),
        fuel_end_pct:       toInt(form.fuel_end_pct),
      };
      // Periode må kun ændres på ikke-GoMore bookings (backend håndhæver også dette)
      if (inv.source !== 'gomore') {
        payload.rental_start = inputToDt(form.rental_start);
        payload.rental_end   = inputToDt(form.rental_end);
      }
      await updateImportedBooking(inv.id, payload);
      // Gem også ekstra-linjer (replace all). Tomme linjer + ugyldige beløb filtreres bort.
      const cleanExtras = editExtras
        .map(e => ({ description: e.description.trim(), amount: parseFloat(e.amount), vat_applicable: e.vat_applicable }))
        .filter(e => e.description.length > 0 && Number.isFinite(e.amount));
      await saveBookingExtras(inv.id, cleanExtras);
      await reloadExtras();
      // Backend genberegner vat_amount automatisk — hent frisk booking-data så ny moms vises
      const fresh = await getBookingById(inv.id);
      setInv(fresh);
      onSave(fresh);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="modal-overlay">
      <div className="modal-panel max-w-lg">

        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="modal-title">{inv.registration_number ?? '—'}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{inv.renter_name ?? '—'} · {formatDate(inv.invoice_date)}</p>
          </div>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {editing ? (
            <div className="space-y-5">
              {error && <p className="text-sm text-[var(--color-error)] mb-2">{error}</p>}

              <Section title={t('rental.detail_invoice_no')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField label={t('rental.detail_invoice_no')} type="text" value={form.invoice_number} onChange={set('invoice_number')} />
                  <FormField label={t('rental.col_date')} type="date" value={form.invoice_date} onChange={set('invoice_date')} />
                </div>
              </Section>

              {/* Periode — kun redigerbar på ikke-GoMore bookings (GoMore styrer deres egne datoer) */}
              {inv.source !== 'gomore' && (
                <Section title={t('rental.detail_rental')}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <FormField label={t('rental.detail_actual_start')} type="datetime-local" value={form.rental_start} onChange={set('rental_start')} />
                    <FormField label={t('rental.detail_actual_end')}   type="datetime-local" value={form.rental_end}   onChange={set('rental_end')} />
                  </div>
                </Section>
              )}

              <Section title={t('rental.detail_economy')}>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label={t('rental.col_rental')}              value={form.rental_amount}       onChange={set('rental_amount')} />
                  <FormField label={t('rental.col_dynamic_pricing')}     value={form.dynamic_pricing}     onChange={set('dynamic_pricing')} />
                  <FormField label={t('rental.col_km_extra_amount')}     value={form.km_extra_amount}     onChange={set('km_extra_amount')} />
                  <FormField label={t('rental.col_km_overage_amount')}   value={form.km_overage_amount}   onChange={set('km_overage_amount')} />
                  <FormField label={t('rental.col_excess')}              value={form.excess_reduction}    onChange={set('excess_reduction')} />
                  <FormField label={t('rental.col_excess_risk')}         value={form.excess_risk_amount}  onChange={set('excess_risk_amount')} />
                  <FormField label={t('rental.col_fuel_adjustment')}     value={form.fuel_adjustment}     onChange={set('fuel_adjustment')} />
                  <FormField label={t('rental.col_late_return_fee')}     value={form.late_return_fee}     onChange={set('late_return_fee')} />
                  <FormField label={t('rental.detail_extra_driver_fee')} value={form.extra_driver_fee}    onChange={set('extra_driver_fee')} />
                  <FormField label={t('rental.detail_foreign_fee')}      value={form.foreign_driving_fee} onChange={set('foreign_driving_fee')} />
                  <FormField label={t('rental.col_fee')}                 value={form.handling_fees}       onChange={set('handling_fees')} />
                  <FormField label={t('rental.col_discount')}            value={form.discount}            onChange={set('discount')} />
                  <FormField label={t('rental.col_payout')}              value={form.payout}              onChange={set('payout')} />
                </div>
                <p className="text-xs text-gray-400 mt-2">Alle beløb er inkl. moms. Heraf-moms beregnes automatisk når du gemmer.</p>
              </Section>

              {/* Ekstra-linjer — frit valgfri tekst + beløb. Bruges når en faktura har poster
                  der ikke passer ind i de faste komponenter (fx "Afhentning i Randers"). */}
              <Section title="Andre poster">
                <div className="space-y-2">
                  {editExtras.map((row, i) => (
                    <div key={i} className="grid grid-cols-[1fr_120px_80px_36px] gap-2 items-end">
                      <div>
                        {i === 0 && <label className="block text-xs text-gray-500 mb-0.5">Beskrivelse</label>}
                        <input type="text" className="input w-full"
                          value={row.description}
                          onChange={e => setEditExtras(prev => prev.map((r, j) => j === i ? { ...r, description: e.target.value } : r))}
                          placeholder='Fx "Afhentning i Randers"' />
                      </div>
                      <div>
                        {i === 0 && <label className="block text-xs text-gray-500 mb-0.5">Beløb (inkl. moms)</label>}
                        <input type="number" step="0.01" className="input w-full"
                          value={row.amount}
                          onChange={e => setEditExtras(prev => prev.map((r, j) => j === i ? { ...r, amount: e.target.value } : r))}
                          placeholder="0,00" />
                      </div>
                      <div>
                        {i === 0 && <label className="block text-xs text-gray-500 mb-0.5">Momspligtig</label>}
                        <label className="flex items-center justify-center h-[34px] cursor-pointer">
                          <input type="checkbox"
                            checked={row.vat_applicable}
                            onChange={e => setEditExtras(prev => prev.map((r, j) => j === i ? { ...r, vat_applicable: e.target.checked } : r))}
                            className="w-4 h-4" />
                        </label>
                      </div>
                      <button type="button" onClick={() => setEditExtras(prev => prev.filter((_, j) => j !== i))}
                        title="Fjern linje"
                        className="w-9 h-9 flex items-center justify-center rounded text-gray-400 hover:bg-rose-50 hover:text-rose-700">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  <button type="button"
                    onClick={() => setEditExtras(prev => [...prev, { id: null, description: '', amount: '', vat_applicable: true }])}
                    className="text-sm text-sage-700 hover:underline">
                    + Tilføj linje
                  </button>
                  <p className="text-xs text-gray-400 mt-1">Sluk "Momspligtig" på linjer der ikke skal indgå i moms (fx skadeerstatning, mistet lejeindtægt, bøder).</p>
                </div>
              </Section>

              <Section title={t('rental.detail_km')}>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label={t('rental.detail_km_start')}           step="1" value={form.km_start}    onChange={set('km_start')} />
                  <FormField label={t('rental.detail_km_end')}             step="1" value={form.km_end}      onChange={set('km_end')} />
                  <FormField label={t('rental.detail_km_included')}        step="1" value={form.km_included} onChange={set('km_included')} />
                  <FormField label={t('rental.detail_km_extra_purchased')} step="1" value={form.km_extra}    onChange={set('km_extra')} />
                </div>
              </Section>

              <Section title={t('rental.detail_fuel')}>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label={t('rental.detail_fuel_start')} step="1" value={form.fuel_start_pct} onChange={set('fuel_start_pct')} />
                  <FormField label={t('rental.detail_fuel_end')}   step="1" value={form.fuel_end_pct}   onChange={set('fuel_end_pct')} />
                </div>
              </Section>
            </div>
          ) : (
            <>
              {(inv.renter_phone || inv.renter_email) && (
                <Section title={t('rental.detail_renter')}>
                  {inv.renter_phone && <Row label={t('rental.detail_renter_phone')} value={inv.renter_phone} />}
                  {inv.renter_email && <Row label={t('rental.detail_renter_email')} value={inv.renter_email} />}
                </Section>
              )}

              <Section title={t('rental.detail_rental')}>
                <Row label={t('rental.col_period')} value={`${formatDatetime(inv.rental_start, tz)} → ${formatDatetime(inv.rental_end, tz)}`} />
                {inv.actual_start_at && <Row label={t('rental.detail_actual_start')} value={formatDatetime(inv.actual_start_at, tz)} />}
                {inv.actual_end_at && <Row label={t('rental.detail_actual_end')} value={formatDatetime(inv.actual_end_at, tz)} />}
                {inv.rental_duration_hours != null && <Row label={t('rental.detail_duration')} value={`${inv.rental_duration_hours} t`} />}
                {inv.actual_duration_hours != null && <Row label={t('rental.detail_actual_duration')} value={`${inv.actual_duration_hours} t`} />}
                <Row label={t('rental.col_source')} value={inv.source === 'gomore' ? 'GoMore' : inv.source ?? '—'} />
                {inv.uid && <Row label="UID (GoMore)" value={inv.uid} />}
                {inv.invoice_number && <Row label={t('rental.detail_invoice_no')} value={inv.invoice_number} />}
                {inv.invoice_date && <Row label={t('rental.col_date')} value={formatDate(inv.invoice_date)} />}
                {inv.cancelled && (
                  <>
                    <Row label={t('rental.detail_cancelled')} value={<span className="text-[var(--color-error)]">{inv.cancelled_at ? formatDate(inv.cancelled_at) : t('rental.detail_yes')}</span>} />
                    {inv.cancellation_fee != null && <Row label={t('rental.detail_cancellation_fee')} value={fmt(inv.cancellation_fee)} />}
                  </>
                )}
              </Section>

              <Section title={t('rental.detail_economy')}>
                <Row label={t('rental.col_rental')} value={fmt(inv.rental_amount)} />
                {inv.dynamic_pricing != null && <Row label={t('rental.col_dynamic_pricing')} value={fmt(inv.dynamic_pricing)} />}
                {inv.km_extra_amount != null && <Row label={t('rental.col_km_extra_amount')} value={fmt(inv.km_extra_amount)} />}
                {inv.km_overage_amount != null && <Row label={t('rental.col_km_overage_amount')} value={fmt(inv.km_overage_amount)} />}
                {inv.discount != null && Number(inv.discount) !== 0 && <Row label={t('rental.col_discount')} value={fmt(inv.discount)} />}
                {inv.excess_reduction != null && (
                  <>
                    {inv.excess_risk_amount != null && <Row label={t('rental.col_excess_risk')} value={fmt(inv.excess_risk_amount)} />}
                    <Row label={t('rental.col_excess')} value={fmt(inv.excess_reduction)} />
                  </>
                )}
                {inv.fuel_adjustment != null && <Row label={t('rental.col_fuel_adjustment')} value={fmt(inv.fuel_adjustment)} />}
                {inv.late_return_fee != null && <Row label={t('rental.col_late_return_fee')} value={fmt(inv.late_return_fee)} />}
                {inv.extra_driver_fee != null && Number(inv.extra_driver_fee) !== 0 && <Row label={t('rental.detail_extra_driver_fee')} value={fmt(inv.extra_driver_fee)} />}
                {inv.foreign_driving_fee != null && Number(inv.foreign_driving_fee) !== 0 && <Row label={t('rental.detail_foreign_fee')} value={fmt(inv.foreign_driving_fee)} />}
                {extras.map(e => (
                  <Row key={e.id} label={
                    <span>
                      {e.description}
                      {!e.vat_applicable && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-gray-400">u/moms</span>}
                    </span>
                  } value={fmt(e.amount)} />
                ))}
                <Row label={t('rental.col_fee')} value={fmt(inv.handling_fees)} />
                <Row label={t('rental.col_payout')} value={<span className="font-semibold">{fmt(inv.payout)}</span>} />
                {/* Udbetaling er hvad GoMore afregner — den kender ikke egne krav lagt på bagefter.
                    Uden denne linje stod fakturalinjerne oplistet ovenfor uden at indgå i nogen sum. */}
                {extras.length > 0 && (
                  <Row
                    label={<span className="font-semibold">Faktureret i alt</span>}
                    value={<span className="font-semibold">
                      {fmt(Number(inv.payout ?? 0) + extras.reduce((s, e) => s + Number(e.amount), 0))}
                    </span>}
                  />
                )}
                {inv.vat_amount != null && Number(inv.vat_amount) !== 0 && (
                  <Row label={<span className="text-gray-400">Heraf moms</span>} value={<span className="text-gray-500">{fmt(inv.vat_amount)}</span>} />
                )}
              </Section>

              {/* Betalinger — viser ALLE posteringer (GoMore + manuelle) på tværs af konti */}
              <Section title={
                <div className="flex items-center justify-between">
                  <span>Betalinger</span>
                  <button onClick={() => setShowAddPayment(true)}
                    className="text-xs text-sage-700 hover:underline inline-flex items-center gap-1 font-normal normal-case tracking-normal">
                    + Tilføj betaling
                  </button>
                </div>
              }>
                {(balance?.postings ?? []).length === 0 ? (
                  <div className="text-sm text-gray-500 py-2">Ingen betalinger registreret. Tilføj en betaling manuelt, eller importér GoMore-kontoudtog.</div>
                ) : (
                  <>
                    <ul className="text-sm divide-y divide-gray-100">
                      {balance!.postings!.map(p => (
                        <li key={p.id} className="flex justify-between items-center py-1.5 group">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 text-gray-800">
                              <span>{p.description}</span>
                              <span className="text-[10px] uppercase tracking-wide text-gray-400 bg-cream-100 rounded px-1.5 py-0.5">{p.account_name}</span>
                            </div>
                            <div className="text-xs text-gray-400">
                              {formatDate(p.posting_date)}
                              {p.match_type === 'manual' && <span className="ml-1.5 text-ink-400">(manuel)</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`font-mono font-medium ${p.amount < 0 ? 'text-rose-700' : 'text-gray-800'}`}>
                              {formatNumber(p.amount, settings.numberFormat)} DKK
                            </div>
                            {/* GoMore-posteringer hører til kontoudtoget og må ikke slettes herfra —
                                kun kobles fra, så de kommer tilbage i match-køen. Skraldespanden er til
                                betalinger man selv har registreret (Revolut, MobilePay …). Begge
                                handlinger bekræftes i to trin. */}
                            {(() => {
                              const isGomore = p.source === 'gomore';
                              if (!isGomore && p.match_type !== 'manual') return null;
                              const confirming = confirmPosting === p.id;
                              const run = () => {
                                if (!confirming) { setConfirmPosting(p.id); return; }
                                setConfirmPosting(null);
                                if (isGomore) handleUnmatchPosting(p.id); else handleDeletePosting(p.id);
                              };
                              if (confirming) {
                                return (
                                  <button onClick={run}
                                    className="text-xs font-medium text-rose-700 underline underline-offset-2 flex-shrink-0">
                                    {t(isGomore ? 'rental.posting_unmatch_confirm' : 'rental.posting_delete_confirm')}
                                  </button>
                                );
                              }
                              return (
                                <button onClick={run}
                                  title={t(isGomore ? 'rental.posting_unmatch_title' : 'rental.posting_delete')}
                                  className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:bg-rose-50 hover:text-rose-700 flex-shrink-0">
                                  {isGomore ? <Unlink size={13} /> : <Trash2 size={13} />}
                                </button>
                              );
                            })()}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 pt-2 border-t border-gray-200 space-y-1">
                      <Row label="Sum modtaget" value={fmt(balance!.received)} />
                      <Row label="Faktura-payout" value={fmt(balance!.payout)} />
                      <Row label="Balance" value={
                        <span className={
                          balance!.balance === 0           ? 'text-sage-700 font-semibold' :
                          balance!.status === 'pending'    ? 'text-ink-700 font-semibold' :
                          balance!.balance < 0             ? 'text-rose-700 font-semibold' :
                                                             'text-amber-700 font-semibold'
                        }
                          title={balance!.status === 'pending'
                            ? t('rentals.balance_pending_title', { amount: fmt(Math.abs(balance!.balance)), days: balance!.days_since_end ?? '—' })
                            : undefined}>
                          {balance!.balance === 0 ? 'Stemmer' :
                           balance!.status === 'pending' ? t('rentals.balance_pending', { amount: fmt(Math.abs(balance!.balance)), days: balance!.days_since_end ?? '—' }) :
                           balance!.balance < 0   ? `Mangler ${fmt(Math.abs(balance!.balance))}` :
                                                    `Overskud ${fmt(balance!.balance)}`}
                        </span>
                      } />
                    </div>
                  </>
                )}
              </Section>

              {showAddPayment && (
                <AddPaymentInline
                  bookingId={inv.id}
                  payout={Number(inv.payout || 0)}
                  receivedSoFar={balance?.received ?? 0}
                  onClose={() => setShowAddPayment(false)}
                  onSaved={async () => { setShowAddPayment(false); await refreshAll(); }}
                />
              )}

              <Section title={t('rental.detail_km')}>
                <Row label={t('rental.detail_km_start')} value={inv.km_start != null ? `${inv.km_start.toLocaleString('da-DK')} km` : '—'} />
                <Row label={t('rental.detail_km_end')} value={inv.km_end != null ? `${inv.km_end.toLocaleString('da-DK')} km` : '—'} />
                {inv.km_included != null && <Row label={t('rental.detail_km_included')} value={`${inv.km_included.toLocaleString('da-DK')} km`} />}
                {inv.km_extra != null && <Row label={t('rental.detail_km_extra_purchased')} value={`${inv.km_extra.toLocaleString('da-DK')} km`} />}
                {inv.km_start != null && inv.km_end != null && inv.km_included != null && (() => {
                  const overage = inv.km_end - inv.km_start - inv.km_included - (inv.km_extra ?? 0);
                  return overage > 0
                    ? <Row label={t('rental.detail_km_overage')} value={<span className="text-orange-600">{overage.toLocaleString('da-DK')} km</span>} />
                    : null;
                })()}
              </Section>

              <Section title={t('rental.detail_fuel')}>
                <Row label={t('rental.detail_fuel_start')} value={inv.fuel_start_pct != null ? `${inv.fuel_start_pct}%` : '—'} />
                <Row label={t('rental.detail_fuel_end')} value={inv.fuel_end_pct != null ? `${inv.fuel_end_pct}%` : '—'} />
                {inv.fuel_start_pct != null && inv.fuel_end_pct != null && inv.fuel_start_pct > inv.fuel_end_pct && (
                  <Row label={t('rental.detail_fuel_missing')} value={<span className="text-orange-600">-{inv.fuel_start_pct - inv.fuel_end_pct}%</span>} />
                )}
              </Section>
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-6 pb-6 pt-2">
          <div>
            {!editing && (
              <button onClick={handleEdit} className="btn btn-secondary">
                {t('rental.edit_btn')}
              </button>
            )}
          </div>
          <div className="flex gap-3">
            {editing ? (
              <>
                <button onClick={handleCancel} disabled={saving} className="btn btn-secondary">
                  {t('modal.cancel')}
                </button>
                <button onClick={handleSave} disabled={saving} className="btn btn-primary disabled:opacity-50">
                  {saving ? '...' : t('modal.save')}
                </button>
              </>
            ) : (
              <button onClick={onClose} className="btn btn-secondary">{t('modal.close')}</button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

// Inline-form til at registrere en manuel betaling. Vises som overlay-modal med konto-dropdown,
// dato (default i dag), beløb (default suggested: payout − allerede modtaget) og valgfri note.
interface AddPaymentProps {
  bookingId: number;
  payout: number;
  receivedSoFar: number;
  onClose: () => void;
  onSaved: () => void;
}

function AddPaymentInline({ bookingId, payout, receivedSoFar, onClose, onSaved }: AddPaymentProps) {
  const [accounts, setAccounts] = useState<PaymentAccount[]>([]);
  const [sourceKey, setSourceKey] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(() => {
    const remaining = Math.round((payout - receivedSoFar) * 100) / 100;
    return remaining > 0 ? String(remaining) : '';
  });
  const [fee, setFee] = useState('');  // valgfri (Revolut, Stripe etc. tager en andel)
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPaymentAccounts()
      .then(list => {
        const active = list.filter(a => a.is_active);
        setAccounts(active);
        // Default-konto: første ikke-GoMore aktive (bruger vil typisk ikke manuelt registrere GoMore)
        const preferred = active.find(a => a.source_key !== 'gomore') || active[0];
        if (preferred) setSourceKey(preferred.source_key);
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Kunne ikke hente konti'));
  }, []);

  const handleSubmit = async () => {
    const num = parseFloat(amount.replace(',', '.'));
    if (!Number.isFinite(num) || num === 0) { setError('Indtast et gyldigt beløb (ikke 0)'); return; }
    if (!sourceKey) { setError('Vælg en konto'); return; }
    const feeNum = fee.trim() === '' ? 0 : parseFloat(fee.replace(',', '.'));
    if (!Number.isFinite(feeNum) || feeNum < 0) { setError('Gebyr skal være 0 eller positivt'); return; }
    setSaving(true);
    setError(null);
    try {
      await createManualPosting({
        booking_id: bookingId,
        source_key: sourceKey,
        posting_date: date,
        amount: num,
        fee: feeNum > 0 ? feeNum : undefined,
        description: description.trim() || undefined,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke gemme');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 sm:p-6 border-b">
          <h2 className="modal-title">Tilføj betaling</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>
        <div className="p-4 sm:p-6 space-y-3">
          <div>
            <label className="label">Konto</label>
            <select className="input w-full" value={sourceKey} onChange={e => setSourceKey(e.target.value)}>
              {accounts.length === 0 && <option value="">— ingen aktive konti —</option>}
              {accounts.map(a => <option key={a.id} value={a.source_key}>{a.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Dato</label>
              <input type="date" className="input w-full" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div>
              <label className="label">Beløb (DKK)</label>
              <input type="number" step="0.01" className="input w-full" value={amount} onChange={e => setAmount(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label">
              Betalingsgebyr <span className="text-xs text-gray-400 font-normal">(valgfri — fx Revolut/Stripe tager X kr af beløbet)</span>
            </label>
            <input type="number" step="0.01" className="input w-full" value={fee} onChange={e => setFee(e.target.value)} placeholder="0,00" />
            <p className="text-xs text-gray-400 mt-1">Lægges til udlejningens øvrige gebyrer og reducerer den forventede udbetaling tilsvarende.</p>
          </div>
          <div>
            <label className="label">Beskrivelse (valgfri)</label>
            <input type="text" className="input w-full" value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Fx 'Leje 1. rate' eller 'Brændstof'" />
          </div>

          {error && <p className="text-sm text-rose-700">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="btn btn-secondary">Annullér</button>
            <button onClick={handleSubmit} disabled={saving || !sourceKey || !amount}
              className="btn btn-primary">
              {saving ? 'Gemmer…' : 'Gem betaling'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
